import { useState, useEffect } from 'react';
import { Ticket, MessageCircle, AlertCircle, CheckCircle, XCircle, Plus } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/auth/hooks';
import NewTicketModal from './components/NewTicketModal';
import TicketDetailsModal from './components/TicketDetailsModal';
import toast from 'react-hot-toast';

interface SupportTicket {
  id: string;
  ticket_number: string;
  subject: string;
  category: 'Technical' | 'Billing' | 'Feature Request' | 'Bug Report' | 'General';
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  status: 'Open' | 'In Progress' | 'Waiting for Customer' | 'Resolved' | 'Closed';
  description: string;
  created_at: string;
  updated_at: string;
}

const categoryIcons = {
  Technical: '🔧',
  Billing: '💰',
  'Feature Request': '✨',
  'Bug Report': '🐛',
  General: '💬',
};

const statusColors = {
  Open: 'bg-red-100 text-red-800',
  'In Progress': 'bg-blue-100 text-blue-800',
  'Waiting for Customer': 'bg-yellow-100 text-yellow-800',
  Resolved: 'bg-green-100 text-green-800',
  Closed: 'bg-slate-100 text-slate-800',
};

const priorityColors = {
  Urgent: 'bg-red-600 text-white',
  High: 'bg-orange-600 text-white',
  Medium: 'bg-yellow-600 text-white',
  Low: 'bg-slate-400 text-white',
};

export default function Support() {
  const { user, business } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    const businessId = business?.id || user?.user_metadata?.business_id;
    if (businessId) {
      loadTickets();
    } else if (user && !businessId) {
      setLoading(false);
    }
  }, [business?.id, user?.user_metadata?.business_id, user]);

  async function loadTickets() {
    const businessId = business?.id || user?.user_metadata?.business_id;
    if (!businessId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data || []);
    } catch (error) {

      toast.error('שגיאה בטעינת הטיקטים');
    } finally {
      setLoading(false);
    }
  }

  const totalTickets = tickets.length;
  const openTickets = tickets.filter(
    (t) => t.status === 'Open' || t.status === 'In Progress' || t.status === 'Waiting for Customer'
  ).length;
  const resolvedTickets = tickets.filter(
    (t) => t.status === 'Resolved' || t.status === 'Closed'
  ).length;

  const filteredTickets = tickets.filter((ticket) => {
    const matchesSearch =
      ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.ticket_number.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'open' && ['Open', 'In Progress', 'Waiting for Customer'].includes(ticket.status)) ||
      (statusFilter === 'resolved' && ['Resolved', 'Closed'].includes(ticket.status));
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">תמיכה</h1>
              <p className="text-sm text-slate-600 mt-1">ניהול פניות ותקלות</p>
            </div>
            <button
              onClick={() => setShowNewTicket(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>פנייה חדשה</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Ticket className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">סך הכל</p>
                <p className="text-2xl font-bold text-slate-900">{totalTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">פתוחים</p>
                <p className="text-2xl font-bold text-slate-900">{openTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">טופלו</p>
                <p className="text-2xl font-bold text-slate-900">{resolvedTickets}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 mb-4 p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="חיפוש לפי נושא או מספר טיקט..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">כל הסטטוסים</option>
              <option value="open">פתוחים</option>
              <option value="resolved">טופלו</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <MessageCircle className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">אין פניות תמיכה</h3>
            <p className="text-slate-600 mb-4">
              {searchQuery || statusFilter !== 'all'
                ? 'לא נמצאו תוצאות לחיפוש'
                : 'עדיין לא פתחת פניות תמיכה'}
            </p>
            {!searchQuery && statusFilter === 'all' && (
              <button
                onClick={() => setShowNewTicket(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>פתח פנייה ראשונה</span>
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">מספר</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">נושא</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">קטגוריה</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">עדיפות</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">סטטוס</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700">תאריך</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-700"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedTicket(ticket.id)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        >
                          {ticket.ticket_number}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-900">{ticket.subject}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span>{categoryIcons[ticket.category]}</span>
                          <span className="text-sm text-slate-700">{ticket.category}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            priorityColors[ticket.priority]
                          }`}
                        >
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            statusColors[ticket.status]
                          }`}
                        >
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">
                          {new Date(ticket.created_at).toLocaleDateString('he-IL')}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedTicket(ticket.id)}
                          className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          פתח
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showNewTicket && (
        <NewTicketModal
          onClose={() => setShowNewTicket(false)}
          onSuccess={() => {
            setShowNewTicket(false);
            loadTickets();
          }}
        />
      )}

      {selectedTicket && (
        <TicketDetailsModal
          ticketId={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdate={loadTickets}
        />
      )}
    </div>
  );
}
